import { FC, useEffect, useMemo, useState } from 'react';
import { createStyles, makeStyles, Typography } from '@material-ui/core';
import { format } from 'date-fns';
import classNames from 'classnames';

import {
  ALERT_HISTORY_HOURS,
  AlertSeverity,
  fetchAlertsHistory,
  MappedAlertHistory,
  MappedTabAlerts,
} from 'src/apis/alertsSummary';

import { METRICS_TABS, MetricsTabId } from './constants';
import {
  MetricsNotification,
  NOTIFICATION_SEVERITY,
  activeAlertLabel,
  alertWindowLabel,
  historyStatusLabel,
  tabLabel,
} from './notifications';

const useStyles = makeStyles(() =>
  createStyles({
    feed: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      background: '#FFFFFF',
    },
    feedHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 12,
      padding: '16px 16px 12px',
      borderBottom: '1px solid #E4E0D8',
    },
    feedTitle: {
      fontSize: 16,
      fontWeight: 600,
      lineHeight: '22px',
      color: '#202226',
    },
    feedCount: {
      color: '#5C6168',
      fontSize: 13,
      lineHeight: '18px',
      marginTop: 2,
    },
    list: {
      overflowY: 'auto',
      maxHeight: 420,
    },
    item: {
      position: 'relative',
      padding: '12px 16px',
      borderBottom: '1px solid #EFEDEB',
      textAlign: 'left',
    },
    itemBody: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    },
    meta: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      flexWrap: 'wrap',
      minHeight: 24,
    },
    severity: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 12,
      fontWeight: 600,
      lineHeight: '16px',
    },
    dot: {
      width: 8,
      height: 8,
      borderRadius: '50%',
      flexShrink: 0,
    },
    source: {
      color: '#5C6168',
      fontSize: 12,
      lineHeight: '16px',
    },
    time: {
      marginLeft: 'auto',
      color: '#8B929A',
      fontSize: 12,
      lineHeight: '16px',
    },
    itemTitle: {
      color: '#202226',
      fontSize: 14,
      fontWeight: 600,
      lineHeight: '20px',
    },
    message: {
      color: '#5C6168',
      fontSize: 13,
      lineHeight: '18px',
    },
    empty: {
      padding: '28px 16px',
      color: '#5C6168',
      fontSize: 14,
      lineHeight: '20px',
    },
    notice: {
      padding: '10px 16px',
      color: '#8A5A00',
      background: '#FFF6E5',
      fontSize: 13,
      lineHeight: '18px',
      borderBottom: '1px solid #E4E0D8',
    },
    noticeError: {
      color: '#B42318',
      background: '#FDECEC',
    },
    viewAll: {
      border: 0,
      borderTop: '1px solid #E4E0D8',
      background: '#F7F5F3',
      color: '#202226',
      fontSize: 13,
      fontWeight: 600,
      lineHeight: '18px',
      padding: '12px 16px',
      cursor: 'pointer',
      fontFamily: 'inherit',
      textAlign: 'left',

      '&:hover': {
        background: '#E4E0D8',
      },
    },
    page: {
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
    },
    pageHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      flexWrap: 'wrap',
    },
    viewToggle: {
      display: 'inline-flex',
      marginLeft: 'auto',
      border: '1px solid #C8C4BC',
      borderRadius: 2,
      overflow: 'hidden',
      background: '#FFFFFF',
    },
    viewOption: {
      border: 0,
      borderRight: '1px solid #C8C4BC',
      background: 'transparent',
      color: '#202226',
      fontSize: 13,
      fontWeight: 500,
      lineHeight: '18px',
      padding: '6px 12px',
      cursor: 'pointer',
      fontFamily: 'inherit',

      '&:last-child': {
        borderRight: 0,
      },

      '&:hover': {
        background: '#F7F5F3',
      },
    },
    viewOptionSelected: {
      color: '#FFFFFF',
      background: 'linear-gradient(0deg, #005FFA -20.98%, #00B5FF 140.22%)',

      '&:hover': {
        color: '#FFFFFF',
        background: 'linear-gradient(0deg, #005FFA -20.98%, #00B5FF 140.22%)',
      },
    },
    statusActive: {
      color: '#027A48',
      fontSize: 12,
      fontWeight: 600,
      lineHeight: '16px',
    },
    statusCleared: {
      color: '#5C6168',
      fontSize: 12,
      fontWeight: 600,
      lineHeight: '16px',
    },
    summary: {
      color: '#5C6168',
      fontSize: 14,
      lineHeight: '20px',
    },
    filters: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
    },
    filter: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      height: 32,
      padding: '0 12px',
      background: '#FFFFFF',
      border: '1px solid #C8C4BC',
      borderRadius: 2,
      color: '#202226',
      fontSize: 13,
      fontWeight: 500,
      cursor: 'pointer',
      fontFamily: 'inherit',

      '&:hover': {
        background: '#F7F5F3',
      },
    },
    filterSelected: {
      color: '#FFFFFF',
      background: 'linear-gradient(0deg, #005FFA -20.98%, #00B5FF 140.22%)',
      borderColor: 'transparent',

      '&:hover': {
        color: '#FFFFFF',
        background: 'linear-gradient(0deg, #005FFA -20.98%, #00B5FF 140.22%)',
      },
    },
    filterCount: {
      minWidth: 18,
      height: 18,
      padding: '0 5px',
      borderRadius: 9,
      background: '#DC5E8A',
      color: '#FFFFFF',
      fontSize: 11,
      fontWeight: 700,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
    },
    filterCountSelected: {
      background: '#FFFFFF',
      color: '#005FFA',
    },
    filterCountCritical: {
      background: '#D92D20',
    },
    filterCountWarning: {
      background: '#E8A317',
    },
    filterCountError: {
      background: '#B42318',
    },
    cards: {
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    },
    card: {
      position: 'relative',
      background: '#FFFFFF',
      border: '1px solid #D8D4CE',
      borderRadius: 2,
      padding: '14px 16px',
    },
  })
);

const formatWhen = (timestamp: number) =>
  format(new Date(timestamp), 'MMM d, H:mm');

const recordingStartedNote = (value: MappedAlertHistory) => {
  const since = Date.parse(value.recordedSince || '');
  const from = Date.parse(value.from);
  if (
    !value.recordedSince ||
    Number.isNaN(since) ||
    Number.isNaN(from) ||
    since <= from + 60_000
  ) {
    return null;
  }

  return `Recording started ${format(new Date(since), 'MMM d, H:mm')}.`;
};

export interface NotificationNotice {
  text: string;
  tone: 'error' | 'stale';
}

interface NotificationRowProps {
  notification: MetricsNotification;
  showSource?: boolean;
  className: string;
}

const NotificationRow: FC<NotificationRowProps> = ({
  notification,
  showSource,
  className,
}) => {
  const classes = useStyles();
  const severity = NOTIFICATION_SEVERITY[notification.severity];

  return (
    <div className={className}>
      <div className={classes.itemBody}>
        <div className={classes.meta}>
          <span className={classes.severity} style={{ color: severity.color }}>
            <span
              className={classes.dot}
              style={{ background: severity.color }}
            />
            {severity.label}
          </span>
          {showSource && (
            <span className={classes.source}>
              {tabLabel(notification.tabId)}
            </span>
          )}
          {notification.history && (
            <span
              className={
                notification.history.endedAt == null
                  ? classes.statusActive
                  : classes.statusCleared
              }
            >
              {historyStatusLabel(notification.history)}
            </span>
          )}
          <span className={classes.time}>
            {formatWhen(notification.timestamp)}
          </span>
        </div>
        <div className={classes.itemTitle}>{notification.title}</div>
        <div className={classes.message}>{notification.message}</div>
      </div>
    </div>
  );
};

interface NotificationFeedProps {
  title: string;
  notifications: MetricsNotification[];
  notice?: NotificationNotice | null;
  countLabel?: string;
  emptyLabel?: string;
  onViewAll?: () => void;
}

export const NotificationFeed: FC<NotificationFeedProps> = ({
  title,
  notifications,
  notice,
  countLabel,
  emptyLabel,
  onViewAll,
}) => {
  const classes = useStyles();

  return (
    <div className={classes.feed}>
      <div className={classes.feedHeader}>
        <div>
          <Typography className={classes.feedTitle}>{title}</Typography>
          <div className={classes.feedCount}>
            {countLabel || activeAlertLabel(notifications.length)}
          </div>
        </div>
      </div>
      {notice && (
        <div
          className={classNames(classes.notice, {
            [classes.noticeError]: notice.tone === 'error',
          })}
        >
          {notice.text}
        </div>
      )}
      {notifications.length === 0 ? (
        <div className={classes.empty}>
          {emptyLabel || 'No active alerts for this section.'}
        </div>
      ) : (
        <div className={classes.list}>
          {notifications.map((notification) => (
            <NotificationRow
              key={notification.id}
              className={classes.item}
              notification={notification}
            />
          ))}
        </div>
      )}
      {onViewAll && (
        <button type='button' className={classes.viewAll} onClick={onViewAll}>
          All notifications
        </button>
      )}
    </div>
  );
};

type NotificationFilter = 'all' | MetricsTabId;

interface AllNotificationsProps {
  notifications: MetricsNotification[];
  tabs: MappedTabAlerts[];
  notice?: NotificationNotice | null;
  summaryLabel?: string;
  emptyLabel?: string;
}

export const AllNotifications: FC<AllNotificationsProps> = ({
  notifications,
  tabs,
  notice,
  summaryLabel,
  emptyLabel,
}) => {
  const classes = useStyles();
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<MappedAlertHistory | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);

  useEffect(() => {
    if (!showHistory) {
      return undefined;
    }

    let cancelled = false;
    setHistoryError(null);

    fetchAlertsHistory(ALERT_HISTORY_HOURS)
      .then((data) => {
        if (!cancelled) {
          setHistory(data);
          setHistoryError(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHistoryError(
            'Unable to load notifications. Check the monitoring API and try again.'
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [showHistory]);

  const historyPending = showHistory && !history && !historyError;
  const sourceNotifications = showHistory
    ? history?.notifications || []
    : notifications;
  const sourceTabs = showHistory ? history?.tabs || [] : tabs;

  const visible = useMemo(() => {
    if (filter === 'all') {
      return sourceNotifications;
    }

    return sourceNotifications.filter((item) => item.tabId === filter);
  }, [filter, sourceNotifications]);

  const totalCount = sourceTabs.reduce((sum, tab) => sum + tab.count, 0);
  const totalWorst: AlertSeverity | null = sourceTabs.some(
    (tab) => tab.worst === 'critical'
  )
    ? 'critical'
    : sourceTabs.some((tab) => tab.worst === 'warning')
    ? 'warning'
    : null;
  const historySummary = history
    ? [
        alertWindowLabel(totalCount, history.hours),
        recordingStartedNote(history),
      ]
        .filter(Boolean)
        .join('. ')
    : null;
  const summaryText = showHistory
    ? historyPending
      ? 'Loading history…'
      : historyError && !history
      ? 'Alerts unavailable'
      : historySummary
    : summaryLabel || `${activeAlertLabel(totalCount)} across health checks`;
  const pageNotice: NotificationNotice | null = showHistory
    ? historyError
      ? { text: historyError, tone: 'error' }
      : history?.stale
      ? {
          text: 'These alerts may be out of date. The rules have not run recently.',
          tone: 'stale',
        }
      : null
    : notice || null;
  const resolvedEmpty = showHistory
    ? historyPending
      ? 'Loading history…'
      : historyError
      ? 'Alerts could not be loaded for this section.'
      : filter === 'all'
      ? 'No historical alerts in the last 24 hours.'
      : 'No historical alerts in this section for the last 24 hours.'
    : emptyLabel || 'No active alerts in this section.';

  return (
    <div className={classes.page}>
      <div className={classes.pageHeader}>
        <Typography className={classes.summary}>{summaryText}</Typography>
        <div
          className={classes.viewToggle}
          role='group'
          aria-label='Active or historical alerts'
        >
          <button
            type='button'
            aria-pressed={!showHistory}
            className={classNames(classes.viewOption, {
              [classes.viewOptionSelected]: !showHistory,
            })}
            onClick={() => setShowHistory(false)}
          >
            Active
          </button>
          <button
            type='button'
            aria-pressed={showHistory}
            className={classNames(classes.viewOption, {
              [classes.viewOptionSelected]: showHistory,
            })}
            onClick={() => setShowHistory(true)}
          >
            History ({ALERT_HISTORY_HOURS} hours)
          </button>
        </div>
      </div>
      {pageNotice && (
        <div
          className={classNames(classes.notice, {
            [classes.noticeError]: pageNotice.tone === 'error',
          })}
        >
          {pageNotice.text}
        </div>
      )}
      <div
        className={classes.filters}
        role='group'
        aria-label='Filter notifications'
      >
        <FilterButton
          label='All'
          count={totalCount}
          tone={totalWorst}
          selected={filter === 'all'}
          onClick={() => setFilter('all')}
        />
        {METRICS_TABS.map((tab) => {
          const status = sourceTabs.find((item) => item.tabId === tab.id);

          return (
            <FilterButton
              key={tab.id}
              label={tab.label}
              count={status?.count || 0}
              tone={status?.worst}
              failed={Boolean(status?.error)}
              selected={filter === tab.id}
              onClick={() => setFilter(tab.id)}
            />
          );
        })}
      </div>
      {visible.length === 0 ? (
        <Typography className={classes.summary}>{resolvedEmpty}</Typography>
      ) : (
        <div className={classes.cards}>
          {visible.map((notification) => (
            <NotificationRow
              key={notification.id}
              className={classes.card}
              notification={notification}
              showSource
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface FilterButtonProps {
  label: string;
  count: number;
  tone?: AlertSeverity | null;
  failed?: boolean;
  selected: boolean;
  onClick: () => void;
}

const FilterButton: FC<FilterButtonProps> = ({
  label,
  count,
  tone,
  failed,
  selected,
  onClick,
}) => {
  const classes = useStyles();
  const toneClass =
    tone === 'critical'
      ? classes.filterCountCritical
      : tone === 'warning'
      ? classes.filterCountWarning
      : undefined;

  return (
    <button
      type='button'
      aria-pressed={selected}
      className={classNames(classes.filter, {
        [classes.filterSelected]: selected,
      })}
      onClick={onClick}
    >
      {label}
      {failed && count === 0 ? (
        <span
          className={classNames(
            classes.filterCount,
            !selected && classes.filterCountError,
            {
              [classes.filterCountSelected]: selected,
            }
          )}
        >
          !
        </span>
      ) : (
        count > 0 && (
          <span
            className={classNames(classes.filterCount, !selected && toneClass, {
              [classes.filterCountSelected]: selected,
            })}
          >
            {count}
          </span>
        )
      )}
    </button>
  );
};
